import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Button,
  TextField,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Comment as CommentIcon,
  Share as ShareIcon,
  MoreVert as MoreVertIcon,
  Add as AddIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  Image as ImageIcon,
  PushPin as PinIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/common/LoadingSpinner';
import CampusPostService, { CampusPost } from '../services/campusPostService';
import ClubPostService, { ClubPost } from '../services/clubPostService';

// Unified post type for both campus and club posts
type UnifiedPost = CampusPost | ClubPost;

interface PostCardProps {
  post: UnifiedPost;
  currentUser: any;
  onLike: (postId: string) => void;
  onComment: (postId: string, comment: string) => void;
  onDelete?: (postId: string) => void;
  onPin?: (postId: string) => void;
}

interface ImageViewerProps {
  images: string[];
  currentIndex: number;
  open: boolean;
  onClose: () => void;
}

const ImageViewer: React.FC<ImageViewerProps> = ({ images, currentIndex, open, onClose }) => {
  const [activeIndex, setActiveIndex] = useState(currentIndex);

  useEffect(() => {
    setActiveIndex(currentIndex);
  }, [currentIndex]);

  const handlePrevious = () => {
    setActiveIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') handlePrevious();
    if (e.key === 'ArrowRight') handleNext();
    if (e.key === 'Escape') onClose();
  };

  if (!open || images.length === 0) return null;

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      onKeyDown={handleKeyDown}
      sx={{
        '& .MuiDialog-paper': {
          bgcolor: 'rgba(0, 0, 0, 0.95)',
          backdropFilter: 'blur(10px)'
        }
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 1000,
            color: 'white',
            bgcolor: 'rgba(0, 0, 0, 0.6)',
            '&:hover': {
              bgcolor: 'rgba(0, 0, 0, 0.8)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Navigation Buttons */}
        {images.length > 1 && (
          <>
            <IconButton
              onClick={handlePrevious}
              sx={{
                position: 'absolute',
                left: 16,
                zIndex: 1000,
                color: 'white',
                bgcolor: 'rgba(0, 0, 0, 0.6)',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.8)'
                }
              }}
            >
              <ChevronLeftIcon fontSize="large" />
            </IconButton>
            <IconButton
              onClick={handleNext}
              sx={{
                position: 'absolute',
                right: 16,
                zIndex: 1000,
                color: 'white',
                bgcolor: 'rgba(0, 0, 0, 0.6)',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.8)'
                }
              }}
            >
              <ChevronRightIcon fontSize="large" />
            </IconButton>
          </>
        )}

        {/* Main Image */}
        <Box
          component="img"
          src={images[activeIndex]}
          alt={`Image ${activeIndex + 1} of ${images.length}`}
          sx={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}
        />

        {/* Image Counter */}
        {images.length > 1 && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 24,
              left: '50%',
              transform: 'translateX(-50%)',
              bgcolor: 'rgba(0, 0, 0, 0.7)',
              borderRadius: 3,
              px: 2,
              py: 1
            }}
          >
            <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
              {activeIndex + 1} / {images.length}
            </Typography>
          </Box>
        )}

        {/* Thumbnail Strip for Multiple Images */}
        {images.length > 1 && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 80,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 1,
              maxWidth: '90%',
              overflowX: 'auto',
              p: 1,
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              borderRadius: 2
            }}
          >
            {images.map((image, index) => (
              <Box
                key={index}
                onClick={() => setActiveIndex(index)}
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: 1,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  border: activeIndex === index ? '3px solid white' : '2px solid transparent',
                  transition: 'border 0.2s ease',
                  flexShrink: 0
                }}
              >
                <Box
                  component="img"
                  src={image}
                  alt={`Thumbnail ${index + 1}`}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

const PostCard: React.FC<PostCardProps> = ({
  post,
  currentUser,
  onLike,
  onComment,
  onDelete,
  onPin
}) => {
  const navigate = useNavigate();
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [imageViewerIndex, setImageViewerIndex] = useState(0);

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInSeconds = (now.getTime() - date.getTime()) / 1000;

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  const handleLike = () => {
    onLike(post.id);
  };

  const handleComment = () => {
    if (commentText.trim()) {
      onComment(post.id, commentText);
      setCommentText('');
      setShowComments(true);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleUserProfileClick = (userId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    if (userId === currentUser?.id) {
      navigate('/profile');
    } else {
      navigate(`/users/${userId}`);
    }
  };

  // Users can only delete their own posts, or faculty can delete any post
  const canDelete = currentUser?.id === post.authorId || currentUser?.role === 'Faculty';
  // Only faculty members can pin posts
  const canPin = currentUser?.role === 'Faculty';
  // Users can only edit their own posts
  const canEdit = currentUser?.id === post.authorId;

  // Handle image click to open viewer
  const handleImageClick = (_imageUrl: string, index: number) => {
    setImageViewerIndex(index);
    setImageViewerOpen(true);
  };

  // Render images based on count - Facebook/Instagram mobile app style
  const renderImages = () => {
    // Handle both images array and single image property (backend compatibility)
    let images: string[] = [];
    if (Array.isArray(post.images) && post.images.length > 0) {
      images = post.images;
    } else if (post.image) {
      images = [post.image];
    }

    if (!images || images.length === 0) return null;

    const imageCount = images.length;

    // Single Image - Full width, responsive height
    if (imageCount === 1) {
      return (
        <Box
          onClick={() => handleImageClick(images[0], 0)}
          sx={{
            width: '100%',
            mb: 0,
            borderRadius: { xs: 0, sm: 2 },
            overflow: 'hidden',
            cursor: 'pointer',
            position: 'relative',
            '&:hover': {
              '& img': {
                transform: 'scale(1.02)',
                transition: 'transform 0.2s ease-in-out'
              }
            }
          }}
        >
          <Box
            component="img"
            src={images[0]}
            alt="Post image"
            loading="lazy"
            sx={{
              width: '100%',
              height: 'auto',
              maxHeight: { xs: 450, sm: 500 },
              objectFit: 'cover',
              display: 'block',
              transition: 'transform 0.2s ease-in-out'
            }}
          />
        </Box>
      );
    }

    // Two Images - Side by side, equal width
    if (imageCount === 2) {
      return (
        <Box sx={{
          display: 'flex',
          gap: { xs: 2, sm: 3 },
          mb: 0,
          borderRadius: { xs: 0, sm: 2 },
          overflow: 'hidden'
        }}>
          {images.slice(0, 2).map((image, index) => (
            <Box
              key={index}
              onClick={() => handleImageClick(image, index)}
              sx={{
                flex: 1,
                cursor: 'pointer',
                borderRadius: { xs: 1.5, sm: 2 },
                overflow: 'hidden',
                '&:hover': {
                  '& img': {
                    transform: 'scale(1.05)',
                    transition: 'transform 0.2s ease-in-out'
                  }
                }
              }}
            >
              <Box
                component="img"
                src={image}
                alt={`Post image ${index + 1}`}
                loading="lazy"
                sx={{
                  width: '100%',
                  height: { xs: 220, sm: 280 },
                  objectFit: 'cover',
                  display: 'block',
                  transition: 'transform 0.2s ease-in-out'
                }}
              />
            </Box>
          ))}
        </Box>
      );
    }

    // Three Images - One large on left, two stacked on right
    if (imageCount === 3) {
      return (
        <Box sx={{
          display: 'flex',
          gap: { xs: 2, sm: 3 },
          height: { xs: 280, sm: 320 },
          mb: 0,
          borderRadius: { xs: 0, sm: 2 },
          overflow: 'hidden'
        }}>
          {/* Main image - 60% width */}
          <Box
            onClick={() => handleImageClick(images[0], 0)}
            sx={{
              flex: '0 0 60%',
              cursor: 'pointer',
              borderRadius: { xs: 1.5, sm: 2 },
              overflow: 'hidden',
              '&:hover': {
                '& img': {
                  transform: 'scale(1.05)',
                  transition: 'transform 0.2s ease-in-out'
                }
              }
            }}
          >
            <Box
              component="img"
              src={images[0]}
              alt="Post image 1"
              loading="lazy"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
                transition: 'transform 0.2s ease-in-out'
              }}
            />
          </Box>

          {/* Side images - 40% width, stacked */}
          <Box sx={{
            flex: '0 0 calc(40% - 3px)',
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 2, sm: 3 }
          }}>
            <Box
              onClick={() => handleImageClick(images[1], 1)}
              sx={{
                flex: 1,
                cursor: 'pointer',
                borderRadius: { xs: 1.5, sm: 2 },
                overflow: 'hidden',
                '&:hover': {
                  '& img': {
                    transform: 'scale(1.05)',
                    transition: 'transform 0.2s ease-in-out'
                  }
                }
              }}
            >
              <Box
                component="img"
                src={images[1]}
                alt="Post image 2"
                loading="lazy"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                  transition: 'transform 0.2s ease-in-out'
                }}
              />
            </Box>
            <Box
              onClick={() => handleImageClick(images[2], 2)}
              sx={{
                flex: 1,
                cursor: 'pointer',
                borderRadius: { xs: 1.5, sm: 2 },
                overflow: 'hidden',
                '&:hover': {
                  '& img': {
                    transform: 'scale(1.05)',
                    transition: 'transform 0.2s ease-in-out'
                  }
                }
              }}
            >
              <Box
                component="img"
                src={images[2]}
                alt="Post image 3"
                loading="lazy"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                  transition: 'transform 0.2s ease-in-out'
                }}
              />
            </Box>
          </Box>
        </Box>
      );
    }

    // Four Images - 2x2 grid
    if (imageCount === 4) {
      return (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gridTemplateRows: '1fr 1fr',
          gap: { xs: 2, sm: 3 },
          height: { xs: 280, sm: 320 },
          mb: 0,
          borderRadius: { xs: 0, sm: 2 },
          overflow: 'hidden'
        }}>
          {images.slice(0, 4).map((image, index) => (
            <Box
              key={index}
              onClick={() => handleImageClick(image, index)}
              sx={{
                cursor: 'pointer',
                borderRadius: { xs: 1.5, sm: 2 },
                overflow: 'hidden',
                '&:hover': {
                  '& img': {
                    transform: 'scale(1.05)',
                    transition: 'transform 0.2s ease-in-out'
                  }
                }
              }}
            >
              <Box
                component="img"
                src={image}
                alt={`Post image ${index + 1}`}
                loading="lazy"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                  transition: 'transform 0.2s ease-in-out'
                }}
              />
            </Box>
          ))}
        </Box>
      );
    }

    // Five or More Images - Large image on left, 2x2 grid on right with overlay
    if (imageCount >= 5) {
      return (
        <Box sx={{
          display: 'flex',
          gap: { xs: 2, sm: 3 },
          height: { xs: 280, sm: 320 },
          mb: 0,
          borderRadius: { xs: 0, sm: 2 },
          overflow: 'hidden'
        }}>
          {/* Main image - 50% width */}
          <Box
            onClick={() => handleImageClick(images[0], 0)}
            sx={{
              flex: '0 0 50%',
              cursor: 'pointer',
              borderRadius: { xs: 1.5, sm: 2 },
              overflow: 'hidden',
              '&:hover': {
                '& img': {
                  transform: 'scale(1.05)',
                  transition: 'transform 0.2s ease-in-out'
                }
              }
            }}
          >
            <Box
              component="img"
              src={images[0]}
              alt="Post image 1"
              loading="lazy"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
                transition: 'transform 0.2s ease-in-out'
              }}
            />
          </Box>

          {/* Grid of 4 images - 50% width */}
          <Box sx={{
            flex: '0 0 calc(50% - 3px)',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gridTemplateRows: '1fr 1fr',
            gap: { xs: 2, sm: 3 }
          }}>
            {images.slice(1, 4).map((image, index) => (
              <Box
                key={index}
                onClick={() => handleImageClick(image, index + 1)}
                sx={{
                  cursor: 'pointer',
                  borderRadius: { xs: 1.5, sm: 2 },
                  overflow: 'hidden',
                  '&:hover': {
                    '& img': {
                      transform: 'scale(1.05)',
                      transition: 'transform 0.2s ease-in-out'
                    }
                  }
                }}
              >
                <Box
                  component="img"
                  src={image}
                  alt={`Post image ${index + 2}`}
                  loading="lazy"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                    transition: 'transform 0.2s ease-in-out'
                  }}
                />
              </Box>
            ))}
            {/* Last image with overlay */}
            <Box
              onClick={() => handleImageClick(images[4], 4)}
              sx={{
                position: 'relative',
                cursor: 'pointer',
                borderRadius: { xs: 1.5, sm: 2 },
                overflow: 'hidden',
                '&:hover': {
                  '& img': {
                    transform: 'scale(1.05)',
                    transition: 'transform 0.2s ease-in-out'
                  }
                }
              }}
            >
              <Box
                component="img"
                src={images[4]}
                alt={`Post image 5`}
                loading="lazy"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                  transition: 'transform 0.2s ease-in-out'
                }}
              />
              {imageCount > 5 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'rgba(0, 0, 0, 0.75)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: { xs: 1.5, sm: 2 },
                    backdropFilter: 'blur(1px)'
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      color: 'white',
                      fontWeight: 800,
                      fontSize: { xs: '1.4rem', sm: '1.6rem' },
                      textShadow: '0 2px 4px rgba(0,0,0,0.8)',
                      letterSpacing: '0.5px'
                    }}
                  >
                    +{imageCount - 5}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      );
    }

    return null;
  };

  return (
    <Card
      sx={{
        mb: { xs: 1, sm: 2 },
        mx: { xs: 0, sm: 0 },
        borderRadius: { xs: 0, sm: 2 },
        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
        border: { xs: 'none', sm: '1px solid #e4e6ea' },
        '&:hover': {
          boxShadow: { xs: '0 1px 2px rgba(0,0,0,0.1)', sm: '0 2px 8px rgba(0,0,0,0.15)' }
        },
        transition: 'box-shadow 0.2s ease-in-out',
        overflow: 'hidden',
        backgroundColor: 'white'
      }}
    >
      {post.isPinned && (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          px: 3,
          py: 1.5,
          bgcolor: '#fff3cd',
          borderBottom: '1px solid #ffeaa7'
        }}>
          <PinIcon sx={{ fontSize: 16, mr: 1, color: '#856404' }} />
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#856404' }}>
            📌 Pinned Post
          </Typography>
        </Box>
      )}

      {/* Header */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        p: { xs: 2, sm: 3 },
        pb: { xs: 1.5, sm: 2 }
      }}>
        <Avatar
          onClick={(e) => handleUserProfileClick(post.authorId, e)}
          sx={{
            bgcolor: '#1877f2',
            width: { xs: 40, sm: 44 },
            height: { xs: 40, sm: 44 },
            mr: { xs: 1.5, sm: 2 },
            fontSize: { xs: '1rem', sm: '1.1rem' },
            fontWeight: 600,
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.8,
              transform: 'scale(1.05)'
            },
            transition: 'all 0.2s ease'
          }}
        >
          {post.authorProfilePicture || post.authorName?.charAt(0) || '👤'}
        </Avatar>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography
              variant="subtitle1"
              onClick={(e) => handleUserProfileClick(post.authorId, e)}
              sx={{
                fontWeight: 600,
                fontSize: { xs: '0.95rem', sm: '1rem' },
                color: '#1877f2',
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                  color: '#166fe5'
                },
                transition: 'color 0.2s ease'
              }}
            >
              {post.authorName || 'Unknown User'}
            </Typography>
            <Chip
              icon={post.authorRole === 'Faculty' ? <SchoolIcon /> : <PersonIcon />}
              label={post.authorRole || 'Student'}
              size="small"
              color={post.authorRole === 'Faculty' ? 'secondary' : 'primary'}
              variant="outlined"
              sx={{
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                height: { xs: 20, sm: 22 }
              }}
            />
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontSize: { xs: '0.8rem', sm: '0.85rem' },
              cursor: 'pointer',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            {formatTime(post.timestamp)}
          </Typography>
        </Box>
        <IconButton onClick={handleMenuOpen} size="small">
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
        >
          {canEdit && (
            <MenuItem onClick={() => { /* TODO: Implement edit functionality */ handleMenuClose(); }}>
              <EditIcon sx={{ mr: 1 }} />
              Edit Post
            </MenuItem>
          )}
          {canPin && onPin && (
            <MenuItem onClick={() => { onPin(post.id); handleMenuClose(); }}>
              <PinIcon sx={{ mr: 1 }} />
              {post.isPinned ? 'Unpin Post' : 'Pin Post'}
            </MenuItem>
          )}
          {canDelete && onDelete && (
            <MenuItem onClick={() => { onDelete(post.id); handleMenuClose(); }} sx={{ color: 'error.main' }}>
              <DeleteIcon sx={{ mr: 1 }} />
              Delete Post
            </MenuItem>
          )}
        </Menu>
      </Box>

      {/* Content */}
      <Box sx={{
        px: { xs: 2, sm: 3 },
        pb: { xs: 1.5, sm: 2 }
      }}>
        <Typography
          variant="body1"
          sx={{
            lineHeight: 1.6,
            fontSize: { xs: '0.95rem', sm: '1rem' },
            mb: { xs: 1.5, sm: 2 },
            color: '#050505',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}
        >
          {post.content}
        </Typography>

        {renderImages()}
      </Box>

      {/* Actions */}
      <Box sx={{
        borderTop: '1px solid #e4e6ea',
        px: { xs: 1, sm: 2 },
        py: { xs: 1, sm: 1.5 }
      }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Button
            startIcon={post.isLiked ? <FavoriteIcon /> : <FavoriteBorderIcon />}
            onClick={handleLike}
            sx={{
              textTransform: 'none',
              borderRadius: { xs: 2, sm: 1.5 },
              px: { xs: 2, sm: 2.5 },
              py: { xs: 1.5, sm: 1 },
              fontSize: { xs: '0.85rem', sm: '0.9rem' },
              fontWeight: 500,
              flex: 1,
              minHeight: { xs: 44, sm: 'auto' }, // Better touch target on mobile
              color: post.isLiked ? '#e41e3f' : '#65676b',
              '&:hover': {
                bgcolor: post.isLiked ? 'rgba(228, 30, 63, 0.1)' : 'rgba(0, 0, 0, 0.05)',
              },
              '&:active': {
                bgcolor: post.isLiked ? 'rgba(228, 30, 63, 0.2)' : 'rgba(0, 0, 0, 0.1)',
              }
            }}
          >
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5
            }}>
              {/* <Box component="span" sx={{ fontSize: { xs: '1.1rem', sm: '1rem' } }}>
                {post.isLiked ? '❤️' : '🤍'}
              </Box> */}
              <Box component="span">
                {post.likes > 0 ? post.likes : 'Like'}
              </Box>
            </Box>
          </Button>

          <Button
            startIcon={<CommentIcon />}
            onClick={() => setShowComments(!showComments)}
            sx={{
              textTransform: 'none',
              borderRadius: { xs: 2, sm: 1.5 },
              px: { xs: 2, sm: 2.5 },
              py: { xs: 1.5, sm: 1 },
              fontSize: { xs: '0.85rem', sm: '0.9rem' },
              fontWeight: 500,
              flex: 1,
              minHeight: { xs: 44, sm: 'auto' },
              color: '#65676b',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.05)',
              },
              '&:active': {
                bgcolor: 'rgba(0, 0, 0, 0.1)',
              }
            }}
          >
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5
            }}>
              {/* <Box component="span" sx={{ fontSize: { xs: '1.1rem', sm: '1rem' } }}>
                💬
              </Box> */}
              <Box component="span">
                {(post.comments?.length || 0) > 0 ? post.comments?.length : 'Comment'}
              </Box>
            </Box>
          </Button>

          {/* <Button
            startIcon={<ShareIcon />}
            sx={{
              textTransform: 'none',
              borderRadius: { xs: 2, sm: 1.5 },
              px: { xs: 2, sm: 2.5 },
              py: { xs: 1.5, sm: 1 },
              fontSize: { xs: '0.85rem', sm: '0.9rem' },
              fontWeight: 500,
              flex: 1,
              minHeight: { xs: 44, sm: 'auto' },
              color: '#65676b',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.05)',
              },
              '&:active': {
                bgcolor: 'rgba(0, 0, 0, 0.1)',
              }
            }}
          >
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5
            }}>
              <Box component="span" sx={{ fontSize: { xs: '1.1rem', sm: '1rem' } }}>
                🔗
              </Box>
              <Box component="span">Share</Box>
            </Box>
          </Button> */}
        </Box>
      </Box>

      {showComments && (
        <>
          <Box sx={{ borderTop: '1px solid #e4e6ea' }}>
            {/* Comments Header */}
            <Box sx={{
              p: { xs: 2, sm: 1.5 },
              pb: { xs: 1.5, sm: 1 },
              borderBottom: '1px solid #f0f2f5'
            }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: '#050505',
                  fontSize: { xs: '0.9rem', sm: '0.85rem' }
                }}
              >
                Comments ({post.comments?.length || 0})
              </Typography>
            </Box>

            {/* Comments List */}
            {post.comments && post.comments.length > 0 && (
              <Box sx={{
                maxHeight: { xs: 300, sm: 250 },
                overflowY: 'auto',
                p: { xs: 2, sm: 1.5 },
                pt: { xs: 1.5, sm: 1 }
              }}>
                {post.comments.map((comment: any, index: number) => (
                  <Box key={comment.id || index} sx={{
                    mb: { xs: 2, sm: 1.5 },
                    p: { xs: 2, sm: 1.5 },
                    borderRadius: { xs: 2, sm: 1.5 },
                    bgcolor: '#f0f2f5',
                    '&:hover': { bgcolor: '#e4e6ea' },
                    transition: 'background-color 0.2s ease'
                  }}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: { xs: 1.5, sm: 1 }
                    }}>
                      <Avatar
                        onClick={(e) => handleUserProfileClick(comment.authorId, e)}
                        sx={{
                          width: { xs: 36, sm: 32 },
                          height: { xs: 36, sm: 32 },
                          bgcolor: '#1877f2',
                          fontSize: { xs: '0.9rem', sm: '0.8rem' },
                          fontWeight: 600,
                          cursor: 'pointer',
                          '&:hover': {
                            opacity: 0.8,
                            transform: 'scale(1.05)'
                          },
                          transition: 'all 0.2s ease'
                        }}>
                        {comment.authorName?.charAt(0) || comment.author?.charAt(0) || '👤'}
                      </Avatar>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: { xs: 1, sm: 0.5 },
                          mb: { xs: 0.5, sm: 0.25 }
                        }}>
                          <Typography
                            variant="body2"
                            onClick={(e) => handleUserProfileClick(comment.authorId, e)}
                            sx={{
                              fontWeight: 600,
                              fontSize: { xs: '0.85rem', sm: '0.8rem' },
                              color: '#1877f2',
                              cursor: 'pointer',
                              '&:hover': {
                                textDecoration: 'underline',
                                color: '#166fe5'
                              },
                              transition: 'color 0.2s ease'
                            }}
                          >
                            {comment.authorName || comment.author}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: { xs: '0.75rem', sm: '0.7rem' } }}
                          >
                            {formatTime(comment.timestamp)}
                          </Typography>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            lineHeight: 1.5,
                            fontSize: { xs: '0.85rem', sm: '0.8rem' },
                            color: '#050505',
                            wordBreak: 'break-word'
                          }}
                        >
                          {comment.content}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {/* Empty Comments State */}
            {(!post.comments || post.comments.length === 0) && (
              <Box sx={{
                p: { xs: 3, sm: 2.5 },
                textAlign: 'center',
                color: 'text.secondary'
              }}>
                <Typography
                  variant="body2"
                  sx={{ fontSize: { xs: '0.9rem', sm: '0.85rem' } }}
                >
                  No comments yet. Be the first to comment!
                </Typography>
              </Box>
            )}

            {/* Add Comment Section */}
            <Box sx={{
              p: { xs: 2, sm: 1.5 },
              pt: { xs: 1.5, sm: 1 },
              borderTop: '1px solid #e4e6ea',
              bgcolor: '#f8f9fa'
            }}>
              <Box sx={{
                display: 'flex',
                gap: { xs: 1.5, sm: 1.5 },
                alignItems: 'flex-start'
              }}>
                <Avatar sx={{
                  width: { xs: 36, sm: 32 },
                  height: { xs: 36, sm: 32 },
                  bgcolor: '#1877f2',
                  fontSize: { xs: '0.9rem', sm: '0.8rem' },
                  fontWeight: 600
                }}>
                  {currentUser?.name?.charAt(0) || '👤'}
                </Avatar>
                <Box sx={{ flex: 1, display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Write a comment..."
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleComment();
                      }
                    }}
                    multiline
                    maxRows={4}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: { xs: 5, sm: 3 },
                        bgcolor: 'white',
                        fontSize: { xs: '0.95rem', sm: '0.9rem' },
                        minHeight: { xs: 44, sm: 'auto' }, // Better touch target
                        '& fieldset': {
                          borderColor: '#e4e6ea'
                        },
                        '&:hover fieldset': {
                          borderColor: '#1877f2'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1877f2'
                        }
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleComment}
                    disabled={!commentText.trim()}
                    sx={{
                      textTransform: 'none',
                      borderRadius: { xs: 5, sm: 3 },
                      minWidth: { xs: 70, sm: 80 },
                      minHeight: { xs: 44, sm: 'auto' },
                      fontWeight: 600,
                      fontSize: { xs: '0.9rem', sm: '0.85rem' },
                      backgroundColor: '#1877f2',
                      '&:hover': {
                        backgroundColor: '#166fe5'
                      },
                      '&:disabled': {
                        backgroundColor: '#e4e6ea',
                        color: '#8a8d91'
                      }
                    }}
                  >
                    Post
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </>
      )}

      {/* Image Viewer Modal */}
      <ImageViewer
        images={(() => {
          let images: string[] = [];
          if (Array.isArray(post.images) && post.images.length > 0) {
            images = post.images;
          } else if (post.image) {
            images = [post.image];
          }
          return images;
        })()}
        currentIndex={imageViewerIndex}
        open={imageViewerOpen}
        onClose={() => setImageViewerOpen(false)}
      />
    </Card>
  );
};

const CreatePostDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSubmit: (content: string, images?: FileList) => void;
}> = ({ open, onClose, onSubmit }) => {
  const [content, setContent] = useState('');
  const [selectedImages, setSelectedImages] = useState<FileList | null>(null);

  const handleSubmit = () => {
    if (content.trim()) {
      onSubmit(content, selectedImages || undefined);
      setContent('');
      setSelectedImages(null);
      onClose();
    }
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setSelectedImages(event.target.files);
    }
  };

  const handleClose = () => {
    setContent('');
    setSelectedImages(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: { xs: 0, sm: 3 },
          maxHeight: { xs: '100vh', sm: '90vh' },
          m: { xs: 0, sm: 2 }
        }
      }}
    >
      <DialogTitle sx={{
        borderBottom: '1px solid #e4e6ea',
        fontSize: { xs: '1.1rem', sm: '1.25rem' },
        fontWeight: 700,
        textAlign: 'center',
        py: { xs: 2, sm: 1.5 }
      }}>
        Create New Post
      </DialogTitle>
      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        <TextField
          fullWidth
          multiline
          rows={4}
          placeholder="What's happening on campus?"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              borderRadius: { xs: 2, sm: 1.5 },
              fontSize: { xs: '1rem', sm: '0.95rem' },
              '& fieldset': {
                borderColor: '#e4e6ea'
              },
              '&:hover fieldset': {
                borderColor: '#1877f2'
              },
              '&.Mui-focused fieldset': {
                borderColor: '#1877f2'
              }
            }
          }}
        />

        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 2, sm: 2 }
        }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={<ImageIcon />}
            sx={{
              textTransform: 'none',
              borderRadius: { xs: 2, sm: 1.5 },
              py: { xs: 1.5, sm: 1 },
              fontSize: { xs: '0.95rem', sm: '0.9rem' },
              fontWeight: 500,
              borderColor: '#e4e6ea',
              color: '#65676b',
              '&:hover': {
                borderColor: '#1877f2',
                backgroundColor: 'rgba(24, 119, 242, 0.04)'
              }
            }}
          >
            Add Photos
            <input
              type="file"
              hidden
              multiple
              accept="image/*"
              onChange={handleImageChange}
            />
          </Button>
          {selectedImages && selectedImages.length > 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: { xs: '0.9rem', sm: '0.85rem' } }}
            >
              {selectedImages.length} photo{selectedImages.length > 1 ? 's' : ''} selected
            </Typography>
          )}
        </Box>

        {selectedImages && selectedImages.length > 0 && (
          <Box sx={{
            mt: 3,
            display: 'flex',
            gap: { xs: 1.5, sm: 1 },
            flexWrap: 'wrap'
          }}>
            {Array.from(selectedImages).map((file, index) => (
              <Box
                key={index}
                sx={{
                  width: { xs: 90, sm: 80 },
                  height: { xs: 90, sm: 80 },
                  borderRadius: { xs: 2, sm: 1 },
                  overflow: 'hidden',
                  border: '1px solid #e4e6ea',
                }}
              >
                <img
                  src={URL.createObjectURL(file)}
                  alt={`Preview ${index + 1}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{
        borderTop: '1px solid #e4e6ea',
        p: { xs: 2, sm: 3 },
        gap: { xs: 1.5, sm: 1 }
      }}>
        <Button
          onClick={handleClose}
          sx={{
            textTransform: 'none',
            borderRadius: { xs: 2, sm: 1.5 },
            px: { xs: 3, sm: 2 },
            py: { xs: 1.5, sm: 1 },
            fontSize: { xs: '0.95rem', sm: '0.9rem' },
            fontWeight: 500,
            color: '#65676b',
            flex: { xs: 1, sm: 'none' }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!content.trim()}
          sx={{
            textTransform: 'none',
            borderRadius: { xs: 2, sm: 1.5 },
            px: { xs: 3, sm: 2 },
            py: { xs: 1.5, sm: 1 },
            fontSize: { xs: '0.95rem', sm: '0.9rem' },
            fontWeight: 600,
            backgroundColor: '#1877f2',
            flex: { xs: 1, sm: 'none' },
            '&:hover': {
              backgroundColor: '#166fe5'
            },
            '&:disabled': {
              backgroundColor: '#e4e6ea',
              color: '#8a8d91'
            }
          }}
        >
          Post
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface FeedPageProps {
  clubId?: string;
  clubName?: string;
  isInsiderFeed?: boolean;
}

const FeedPage: React.FC<FeedPageProps> = ({ clubId, isInsiderFeed = false }) => {
  const [createPostOpen, setCreatePostOpen] = useState(false);
  const [posts, setPosts] = useState<UnifiedPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const { user } = useAuth();

  const loadPosts = useCallback(async (pageNum: number = 1, reset: boolean = false) => {
    console.log('🔄 Loading posts - Page:', pageNum, 'Reset:', reset, 'IsInsiderFeed:', isInsiderFeed);
    setLoading(true);
    setError(null);

    try {
      let response;

      if (isInsiderFeed && clubId) {
        // Use ClubPostService for insider feed
        response = await ClubPostService.getClubPosts(clubId, pageNum, 10);
      } else {
        // Use CampusPostService for campus feed
        response = await CampusPostService.getCampusPosts(pageNum, 10);
      }

      // Handle the API structure: { data: [...], pagination: {...} }
      const responseData = Array.isArray(response.data) ? response.data : [];
      console.log('📊 Got', responseData.length, 'posts from API');

      if (reset) {
        setPosts(responseData);
      } else {
        setPosts(prev => [...prev, ...responseData]);
      }

      // Check if there are more posts based on pagination info
      const pagination = response.pagination;
      const totalPages = Math.ceil(pagination.total / pagination.limit);
      setHasMore(pageNum < totalPages);
      setPage(pageNum);
      console.log('📄 Page', pageNum, 'of', totalPages, '- Has more:', pageNum < totalPages);
    } catch (error: any) {
      console.error('❌ Failed to load posts:', error);
      setError('Failed to load posts. Please try again.');
      setPosts([]); // Set to empty array on error
    } finally {
      setLoading(false);
    }
  }, [isInsiderFeed, clubId]);

  useEffect(() => {
    loadPosts(1, true);
  }, [loadPosts]);

  const handleCreatePost = async (content: string, images?: FileList) => {
    try {
      console.log('📝 Creating new post...', isInsiderFeed ? 'Club Post' : 'Campus Post');
      setError(null);

      let newPost: UnifiedPost;
      if (isInsiderFeed && clubId) {
        // Create club post
        newPost = await ClubPostService.createClubPost(clubId, { content, images });
      } else {
        // Create campus post
        newPost = await CampusPostService.createCampusPost({ content, images });
      }

      // Validate the post before adding
      if (!newPost || !newPost.id) {
        console.error('❌ Invalid post returned from server:', newPost);
        setError('Failed to create post. Invalid response from server.');
        return;
      }

      console.log('✅ Post created successfully:', newPost);
      setPosts(prev => [newPost, ...prev]);
    } catch (error: any) {
      console.error('❌ Failed to create post:', error);
      setError('Failed to create post. Please try again.');
    }
  };

  const handleLike = async (postId: string) => {
    // Optimistically update UI first
    setPosts(prev => Array.isArray(prev) ? prev.map(post => {
      if (post.id === postId) {
        const newIsLiked = !post.isLiked;
        const newLikes = newIsLiked ? post.likes + 1 : Math.max(0, post.likes - 1);
        return { ...post, isLiked: newIsLiked, likes: newLikes };
      }
      return post;
    }) : []);

    // Try to sync with backend, but don't revert UI if it fails
    try {
      if (isInsiderFeed && clubId) {
        await ClubPostService.toggleLike(postId);
      } else {
        await CampusPostService.toggleLike(postId);
      }
    } catch (error: any) {
      console.error('❌ Failed to sync like with server:', error);
      // Could add a toast notification here if needed
    }
  };

  const handleComment = async (postId: string, comment: string) => {
    try {
      if (isInsiderFeed && clubId) {
        await ClubPostService.addComment(postId, comment);
      } else {
        await CampusPostService.addComment(postId, comment);
      }
      // Reload posts to get updated comments
      loadPosts(1, true);
    } catch (error: any) {
      console.error('❌ Failed to add comment:', error);
    }
  };

  const handleDelete = async (postId: string) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        if (isInsiderFeed && clubId) {
          await ClubPostService.deletePost(postId);
        } else {
          await CampusPostService.deletePost(postId);
        }
        setPosts(prev => Array.isArray(prev) ? prev.filter(post => post.id !== postId) : []);
      } catch (error: any) {
        console.error('Failed to delete post:', error);
        setError('Failed to delete post. Please try again.');
      }
    }
  };

  const handlePin = async (postId: string) => {
    try {
      let result: { isPinned: boolean };
      if (isInsiderFeed && clubId) {
        result = await ClubPostService.togglePin(postId);
      } else {
        result = await CampusPostService.togglePin(postId);
      }

      setPosts(prev => prev.map(post =>
        post.id === postId
          ? { ...post, isPinned: result.isPinned }
          : post
      ));
    } catch (error: any) {
      console.error('Failed to pin post:', error);
    }
  };

  const handleLoadMore = () => {
    if (hasMore && !loading) {
      loadPosts(page + 1, false);
    }
  };

  console.log('🎨 Render - Posts:', posts.length, 'items, Loading:', loading);

  if (loading && posts.length === 0) {
    return <LoadingSpinner fullScreen message="Loading feed..." />;
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: '#f0f2f5',
      pb: { xs: 10, sm: 2 } // Extra padding on mobile for FAB
    }}>
      {/* Mobile Header - Sticky (only for campus feed) */}
      {!isInsiderFeed && (
        <Box sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          backgroundColor: 'white',
          borderBottom: '1px solid #e4e6ea',
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}>
            <Typography
              variant="h5"
              component="h1"
              sx={{
                fontWeight: 700,
                color: '#1877f2',
                fontSize: { xs: '1.5rem', sm: '1.75rem' },
                textAlign: { xs: 'center', sm: 'left' }
              }}
            >
              Campus Feed
            </Typography>
          </Box>
        </Box>
      )}

      {/* Main Content */}
      <Box sx={{
        maxWidth: { xs: '100%', sm: 680 },
        mx: 'auto',
        px: { xs: 0, sm: 2 },
        pt: { xs: isInsiderFeed ? 0 : 2, sm: isInsiderFeed ? 1 : 3 }
      }}>

        {/* Error Alert */}
        {error && (
          <Alert
            severity="error"
            sx={{
              mx: { xs: 2, sm: 0 },
              mb: 2,
              borderRadius: { xs: 0, sm: 1 }
            }}
          >
            {error}
          </Alert>
        )}

        {/* Create Post Card - Facebook Style */}
        <Card sx={{
          mb: { xs: 1, sm: 2 },
          mx: { xs: 0, sm: 0 },
          borderRadius: { xs: 0, sm: 2 },
          boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
          border: { xs: 'none', sm: '1px solid #e4e6ea' }
        }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  bgcolor: '#1877f2',
                  width: { xs: 40, sm: 44 },
                  height: { xs: 40, sm: 44 },
                  fontSize: '1rem',
                  fontWeight: 600
                }}
              >
                {user?.name?.charAt(0) || '👤'}
              </Avatar>
              <Box
                onClick={() => setCreatePostOpen(true)}
                sx={{
                  flex: 1,
                  backgroundColor: '#f0f2f5',
                  borderRadius: 5,
                  px: 2,
                  py: 1.5,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#e4e6ea'
                  },
                  transition: 'background-color 0.2s ease'
                }}
              >
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}
                >
                  What's happening on campus?
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Posts */}
        {posts.length > 0 ? (
          <Box sx={{ pb: { xs: 2, sm: 0 } }}>
            {posts.filter(post => post && post.id).map((post) => (
              <PostCard
                key={post.id}
                post={post}
                currentUser={user}
                onLike={handleLike}
                onComment={handleComment}
                onDelete={handleDelete}
                onPin={handlePin}
              />
            ))}

            {/* Load More Button */}
            {hasMore && (
              <Box sx={{
                textAlign: 'center',
                mt: { xs: 2, sm: 3 },
                px: { xs: 2, sm: 0 }
              }}>
                <Button
                  variant="outlined"
                  onClick={handleLoadMore}
                  disabled={loading}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 3,
                    px: 4,
                    py: 1.5,
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    fontWeight: 500
                  }}
                >
                  {loading ? 'Loading...' : 'Load More Posts'}
                </Button>
              </Box>
            )}
          </Box>
        ) : !loading ? (
          <Card sx={{
            textAlign: 'center',
            py: { xs: 6, sm: 8 },
            mx: { xs: 2, sm: 0 },
            borderRadius: { xs: 2, sm: 3 },
            boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
          }}>
            <CardContent>
              <Typography variant="h5" sx={{
                fontWeight: 600,
                mb: 2,
                fontSize: { xs: '1.3rem', sm: '1.5rem' }
              }}>
                Welcome to Campus Feed! 🎉
              </Typography>
              <Typography variant="body1" sx={{
                mb: 3,
                color: 'text.secondary',
                fontSize: { xs: '0.95rem', sm: '1rem' },
                lineHeight: 1.6
              }}>
                Connect with your campus community.<br />
                Share updates, events, and stay informed!
              </Typography>
              <Button
                variant="contained"
                onClick={() => setCreatePostOpen(true)}
                sx={{
                  textTransform: 'none',
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  fontWeight: 600,
                  backgroundColor: '#1877f2',
                  '&:hover': {
                    backgroundColor: '#166fe5'
                  }
                }}
              >
                Create Your First Post
              </Button>
            </CardContent>
          </Card>
        ) : null}
      </Box>

      {/* Create Post FAB - Mobile Only (visible for both campus and insider feed) */}
      <Fab
        color="primary"
        aria-label="create post"
        onClick={() => setCreatePostOpen(true)}
        sx={{
          position: 'fixed',
          bottom: { xs: 20, sm: 32 },
          right: { xs: 20, sm: 32 },
          width: { xs: 56, sm: 64 },
          height: { xs: 56, sm: 64 },
          backgroundColor: '#1877f2',
          '&:hover': {
            backgroundColor: '#166fe5'
          },
          boxShadow: '0 4px 12px rgba(24, 119, 242, 0.4)',
          display: { xs: 'flex', sm: 'none' } // Hide on desktop since we have the create post card
        }}
      >
        <AddIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
      </Fab>

      {/* Create Post Dialog */}
      <CreatePostDialog
        open={createPostOpen}
        onClose={() => setCreatePostOpen(false)}
        onSubmit={handleCreatePost}
      />
    </Box>
  );
};

export default FeedPage;